import { CuisineCarousel } from 'components/carousels/cuisine-carousel';
import { DietaryCarousel } from 'components/carousels/dietary-carousel';
import { DishesCarousel } from 'components/carousels/dishes-carousel';
import { ShefsCarousel } from 'components/carousels/shefs-carousel';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import {
  generateZipPageH2,
  generateZipPageH2CopyBlock,
  generateZipPageMetaDesc,
} from 'components/dynamic-content/header-copy/zip-page-copy';
import { generateHeroText } from 'components/dynamic-content/hero-copy';
import {
  generateCuisinePositioningCopy,
  generateDishesPositioningCopy,
  generateShefsPositioningCopy,
} from 'components/dynamic-content/positioning-copy';
import { generateZipPageTitle } from 'components/dynamic-content/title-copy';
import { WavyDivider } from 'components/flair/WavyDivider';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import Head from 'next/head';
import React from 'react';
import { Crumb, generateZipPageBreadcrumbs } from 'utils/breadcrumb-util';
import { CuisineFilterButton } from 'utils/cuisine-util';
import { DietaryFilterButton } from 'utils/dietary-util';
import { getFrontEndUrl } from 'utils/environment-util';
import { CityPageDishFragment, CityPageFilterFragment, CityPageShefFragment } from 'utils/gqlReactTypings.generated.d';
import { ZipPage } from 'utils/types';

interface IProps {
  zipCode: string;
  zipPage: ZipPage;
  dishes?: CityPageDishFragment[];
  shefs?: CityPageShefFragment[];
  totalDishesCount?: number;
  cuisineButtons: CuisineFilterButton[];
  dietaryButtons: DietaryFilterButton[];
  activeFilter?: CityPageFilterFragment;
}

export const ZipPageLayout: React.FC<IProps> = ({
  zipCode,
  zipPage,
  dishes,
  shefs,
  totalDishesCount,
  cuisineButtons,
  dietaryButtons,
  activeFilter,
}) => {
  const explorePageLink = `${getFrontEndUrl()}/explore/zip/${zipCode}`;

  const crumbs: Crumb[] = generateZipPageBreadcrumbs(zipCode, zipPage, activeFilter);

  const title = generateZipPageTitle(zipCode, zipPage, activeFilter);

  const numCuisines = cuisineButtons.length;

  return (
    <Layout>
      <Head>
        <title>{title}</title>
        <meta name='og:title' content={title} />
        <meta name='description' content={generateZipPageMetaDesc({ zipPage, zipCode, activeFilter })} />
      </Head>
      <HeroBlock
        className='mb-10'
        heroText={generateHeroText(zipCode, activeFilter)}
        crumbs={crumbs}
        zipCode={zipCode}
        prefillZipCode
      />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-10'
          title={generateZipPageH2({ zipCode, activeFilter })}
          content={generateZipPageH2CopyBlock({
            zipPage,
            zipCode,
            cuisineButtons,
            numDishes: totalDishesCount,
            activeFilter,
          })}
        />
        <WavyDivider className='mb-10' />
      </MaxWidthContainer>
      <CuisineCarousel
        id='cuisine-carousel'
        className='mb-10'
        cuisineButtons={cuisineButtons}
        activeFilter={activeFilter}
        title={`Popular Cuisines in ${zipCode}`}
        positioningCopy={generateCuisinePositioningCopy(zipPage, activeFilter)}
      />
      <DietaryCarousel
        id='dietary-carousel'
        className='mb-20'
        dietaryButtons={dietaryButtons}
        title='Dietary Filters'
        activeFilter={activeFilter}
      />
      <DishesCarousel
        className='mb-20'
        dishes={dishes}
        positioningCopy={generateDishesPositioningCopy({
          cityPage: zipPage,
          numCuisines,
          numDishes: totalDishesCount,
          activeFilter,
        })}
        seeAllHref={explorePageLink}
        activeFilter={activeFilter}
      />
      <ShefsCarousel
        className='mb-20'
        shefs={shefs}
        positioningCopy={generateShefsPositioningCopy(zipPage, activeFilter)}
        seeAllHref={explorePageLink}
        activeFilter={activeFilter}
      />
    </Layout>
  );
};
